import { capitalCase } from 'change-case'
import produce from 'immer'

import {
  type FilterManagerFilter,
  type FilterManagerFilterTag,
  type FilterManagerState,
  type IFilterManagerFilters,
  type UseFilterManagerActions,
} from './types'
import { type UseFilterManagerOptions } from './useFilterManager'

export const convertFiltersToFilterTags = (
  filters: IFilterManagerFilters,
  options: UseFilterManagerOptions<any>
): FilterManagerFilterTag[] => {
  const filterTags = []

  Object.keys(filters).forEach((key) => {
    if (Array.isArray(filters[key].value)) {
      filterTags.push(...convertFilterToFilterTags(filters[key], options))
    } else {
      filterTags.push(convertFilterToFilterTag(filters[key], options))
    }
  })

  const combinedTags = buildCombinedFilterTags(filters, options)

  return [...filterTags, ...combinedTags]
}

const convertFilterToFilterTag = (
  filter: FilterManagerFilter,
  { formatLabels, formatTooltips, lockedTags }: UseFilterManagerOptions
): FilterManagerFilterTag => {
  return {
    ...filter,
    label:
      formatLabels[filter.key] && filter.__resolved
        ? formatLabels[filter.key].call(null, filter.value)
        : capitalCase(filter.key),
    tooltip:
      formatTooltips[filter.key] && filter.__resolved
        ? formatTooltips[filter.key].call(null, filter.value)
        : capitalCase(filter.key),
    locked: !!lockedTags[filter.key],
  }
}

const convertFilterToFilterTags = (
  filter: FilterManagerFilter,
  { formatLabels, formatTooltips, lockedTags }: UseFilterManagerOptions
): FilterManagerFilterTag[] => {
  return filter.value.map((filterValue) => {
    return {
      ...filter,
      value: filterValue,
      label:
        formatLabels[filter.key] && filter.__resolved
          ? formatLabels[filter.key].call(null, filterValue)
          : `${capitalCase(filter.key)}: ${capitalCase(
              filterValue?.toString()
            )}`,
      tooltip:
        formatTooltips[filter.key] && filter.__resolved
          ? formatTooltips[filter.key].call(null, filterValue)
          : capitalCase(filter.key),
      locked: !!lockedTags[filter.key],
    }
  })
}

/**
 * Return an array of tags that are combined
 */
const buildCombinedFilterTags = (
  filters: IFilterManagerFilters,
  {
    formatLabels,
    formatTooltips,
    combineTags: combineTags,
  }: UseFilterManagerOptions
): FilterManagerFilterTag[] => {
  const combineTagKeys = Object.keys(combineTags)

  if (combineTagKeys.length === 0) return []

  const newCombinedTags: FilterManagerFilterTag[] = []

  combineTagKeys.forEach((combineTagKey) => {
    const fieldsToCombine = combineTags[combineTagKey]

    /**
     * We only generate combined tags if all of the combining
     * params has values
     */
    if (
      fieldsToCombine.some((fieldToCombine) => {
        return !!filters[fieldToCombine.toString()]?.value
      })
    ) {
      const fieldsData = {}

      fieldsToCombine.map((fieldToCombine) => {
        fieldsData[fieldToCombine] = filters[fieldToCombine.toString()]
          ?.__resolved
          ? filters[fieldToCombine.toString()]?.value
          : null
      })

      newCombinedTags.push({
        key: combineTagKey,
        paramValue: null,
        value: null,
        label: formatLabels[combineTagKey]
          ? formatLabels[combineTagKey].call(null, fieldsData)
          : capitalCase(combineTagKey),
        tooltip: formatTooltips[combineTagKey]
          ? formatTooltips[combineTagKey].call(null, fieldsData)
          : capitalCase(combineTagKey),
      })
    }
  })

  return newCombinedTags
}

export const convertFiltersToQueryParams = (filters: IFilterManagerFilters) => {
  return Object.values(filters).reduce((prev, filter) => {
    prev[filter.key] = filter.paramValue
    return prev
  }, {})
}

export const reducer = (
  state: FilterManagerState,
  action: UseFilterManagerActions
): FilterManagerState => {
  switch (action.type) {
    case 'ADD_FILTER':
      return produce(state, (draft) => {
        const newFilters = {
          ...state.filters,
          ...action.filter,
        }
        const newFilterTags = convertFiltersToFilterTags(
          newFilters,
          action.options
        )

        const newQueryParams = convertFiltersToQueryParams(newFilters)

        draft.filters = newFilters
        draft.filterTags = newFilterTags
        draft.queryParams = newQueryParams
      })
    case 'REMOVE_FILTERS':
      return produce(state, (draft) => {
        let newFilters = {
          ...state.filters,
        }

        const newFilterTags = state.filterTags.slice()

        const filtersToRemove = action.filters

        /**
         * When removing a combined tag, we need to also
         * find and remove the tags that were combined
         */
        action.filters.forEach((filter) => {
          if (action.options.combineTags?.[filter.key]) {
            const combinedTagsToRemove = action.options.combineTags?.[
              filter.key
            ].map((combinedParam) => ({
              key: combinedParam.toString(),
              paramValue: null,
              value: null,
            }))

            filtersToRemove.push(...combinedTagsToRemove)
          }
        })

        filtersToRemove.forEach((filter) => {
          const valueToRemove = filter.value
          const isCombinedTagRemoval =
            valueToRemove === null && filter.paramValue === null

          const tagIndex = newFilterTags.findIndex((tag) => {
            if (isCombinedTagRemoval) {
              return tag.key === filter.key
            }

            // Find the tag that matches both key AND value. This is necessary for array filters.
            return tag.key === filter.key && tag.value === valueToRemove
          })

          if (tagIndex < 0) return

          newFilterTags.splice(tagIndex, 1)

          const existingFilter = newFilters[filter.key]
          if (!existingFilter) return

          const isArrayFilter = Array.isArray(existingFilter.value)
          const isImproveBillFilteringEnabled =
            action.options.improveBillFilteringEnabled

          if (
            isCombinedTagRemoval ||
            !isArrayFilter ||
            !isImproveBillFilteringEnabled
          ) {
            const { [filter.key]: _removedKey, ...otherParams } = newFilters
            newFilters = otherParams
          } else {
            // Removes specific value from array filter
            const remainingValues = existingFilter.value.filter(
              (value) => value !== valueToRemove
            )

            if (remainingValues.length === 0) {
              const { [filter.key]: _removedKey, ...otherParams } = newFilters
              newFilters = otherParams
            } else {
              const remainingParamValues = remainingValues.map((value) => {
                if (
                  typeof value === 'object' &&
                  value !== null &&
                  'id' in value
                ) {
                  return value.id.toString()
                }
                return String(value)
              })

              newFilters[filter.key] = {
                ...existingFilter,
                value: remainingValues,
                paramValue: remainingParamValues,
              }
            }
          }
        })

        const newQueryParams = convertFiltersToQueryParams(newFilters)

        draft.filters = newFilters
        draft.filterTags = newFilterTags
        draft.queryParams = newQueryParams
      })
    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: {},
        filterTags: [],
        queryParams: {},
      }
  }
}
